/**
 * ToneJs Plugin
 *
 * Provides Tone.js synth nodes.
 * Automatically uses the correct Tone.js version based on environment.
 * Depends on Audio Base Plugin for AudioEndpoint.
 */

import type { Plugin } from '@plugins/Plugin';
import type { Dependencies } from '@nodes/Dependencies';
import type { INode } from '@nodes/INode';
import { isNode } from '@runtime/env';
import { BaseDependencies } from '@nodes/Dependencies';

// Import node definitions
import { synthNodeDef } from './nodes/SynthNode/SynthNode';
import { fmSynthNodeDef } from './nodes/FMSynthNode/FMSynthNode';

/**
 * Tone.js library interface.
 * Both 'tone' (web) and 'tone-node' provide these classes.
 */
export interface ToneLibrary {
  PolySynth: any;
  Synth: any;
  FMSynth: any;
  Frequency: (value: number | string, units?: string) => { toNote(): string };
  Time: (value: number | string) => { toSeconds(): number };
  now: () => number;
  /** Connect with Tone.js bridging (handles Tone nodes <-> Web Audio nodes) */
  connect: (source: any, dest: any) => void;
  /** Tone.js context (for accessing rawContext) */
  context?: any;
  getContext?: () => any;
}

/**
 * Dependencies for ToneJS nodes.
 * Extends base Dependencies with the Tone.js library.
 */
export interface ToneJsDependencies extends Dependencies {
  tone: ToneLibrary;
}

const namespace = 'tone';

const nodes = [
  synthNodeDef,
  fmSynthNodeDef
];

// Lazy-loaded Tone library
let toneLib: ToneLibrary | null = null;

/**
 * Get the Tone.js library for the current environment.
 * Lazily loads on first access.
 */
async function getTone(): Promise<ToneLibrary> {
  if (toneLib) return toneLib;

  if (isNode) {
    // Node.js - use tone-node
    const { PolySynth } = await import('@tone-node/instrument/PolySynth');
    const { Synth } = await import('@tone-node/instrument/Synth');
    const { FMSynth } = await import('@tone-node/instrument/FMSynth');
    const { Frequency } = await import('@tone-node/core/type/Frequency');
    const { Time } = await import('@tone-node/core/type/Time');
    const { connect } = await import('@tone-node/core');
    const { getContext } = await import('@tone-node/core/Global');

    toneLib = {
      PolySynth,
      Synth,
      FMSynth,
      Frequency: (value: any, units?: string) => Frequency(value, units as any),
      Time: (value: any) => Time(value),
      now: () => getContext().now(),
      connect,
      getContext
    };
  } else {
    // Browser - use tone with our audio context
    const Tone = await import('tone');
    const { getAudioContext } = await import('@/env/web/AudioContext');

    // Ensure Tone uses our shared audio context
    const ctx = getAudioContext();
    if (ctx) {
      Tone.setContext(ctx as any);
    }

    toneLib = {
      PolySynth: Tone.PolySynth,
      Synth: Tone.Synth,
      FMSynth: Tone.FMSynth,
      Frequency: (value: any, units?: string) => Tone.Frequency(value, units as any),
      Time: (value: any) => Tone.Time(value),
      now: () => Tone.now(),
      connect: Tone.connect,
      getContext: Tone.getContext
    };
  }

  return toneLib;
}

// Synchronous getter (returns null if not loaded yet)
function getToneSync(): ToneLibrary | null {
  return toneLib;
}

/**
 * ToneJs plugin - uses environment-appropriate Tone.js automatically.
 */
export const tonejsPlugin: Plugin = {
  id: 'tonejs',
  name: 'Tone.js Audio',
  namespace,
  nodes,

  async onRegister() {
    // Pre-load Tone library on plugin registration
    await getTone();
  },

  createNode(type: string, id: string, deps: BaseDependencies): INode {
    const tone = getToneSync();
    if (!tone) {
      throw new Error('Tone.js not loaded - ensure plugin is registered before creating nodes');
    }

    const nodeDef = nodes.find(n => n.type === type);
    if (!nodeDef) {
      throw new Error(`Unknown tonejs node type: ${type}`);
    }
    return new nodeDef.ctor(id, { ...deps, type: `${namespace}.${type}`, tone } as ToneJsDependencies);
  },

  async registerUI({ registerViz }) {
    // ToneJS synths use envelope visualization from audio plugin
    const { EnvelopeVisualization } = await import('../audio/ui/EnvelopeVisualization');
    registerViz('Synth', EnvelopeVisualization);
    registerViz('FMSynth', EnvelopeVisualization);
  }
};
